import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Bitwise() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">⚙️ JavaScript Bitwise Operators</h1>
      <p className="tutorial-intro">
        JavaScript Bitwise Operators perform operations on binary
        representations of numbers (32-bit integers). These operators work
        directly on the bits of numbers.
      </p>

      {/* Two-column Key Section */}
      <div className="key-section bitwise-key-section">
        <button
          onClick={() =>
            document
              .getElementById("bit_and")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          AND (&)
        </button>
        <button
          onClick={() =>
            document
              .getElementById("bit_or")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          OR (|)
        </button>
        <button
          onClick={() =>
            document
              .getElementById("bit_xor")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          XOR (^)
        </button>
        <button
          onClick={() =>
            document
              .getElementById("bit_not")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          NOT (~)
        </button>
        <button
          onClick={() =>
            document
              .getElementById("bit_left")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          Left Shift (&lt;&lt;)
        </button>
        <button
          onClick={() =>
            document
              .getElementById("bit_right")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          Right Shift (&gt;&gt;)
        </button>
        <button
          onClick={() =>
            document
              .getElementById("bit_unsigned")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          Unsigned Right Shift (&gt;&gt;&gt;)
        </button>
      </div>

      {/* 1️⃣ Bitwise AND */}
      <section id="bit_and" className="method-section">
        <h2>
          1️⃣ Bitwise AND (<code className="highlight_code">&amp;</code>)
        </h2>
        <p>
          Performs a logical AND operation on each pair of bits. Only if both
          bits are 1, the result is 1.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let a = 5;  // 0101
let b = 1;  // 0001
console.log(a & b); // 0001 -> 1`}
          />
        </div>
        <TryItButton code={`let a = 5, b = 1; console.log(a & b);`} />
        <OutputBox>1</OutputBox>
      </section>

      {/* 2️⃣ Bitwise OR */}
      <section id="bit_or" className="method-section">
        <h2>
          2️⃣ Bitwise OR (<code className="highlight_code">|</code>)
        </h2>
        <p>
          Performs a logical OR operation on each pair of bits. If at least one
          bit is 1, the result is 1.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let a = 5;  // 0101
let b = 1;  // 0001
console.log(a | b); // 0101 -> 5`}
          />
        </div>
        <TryItButton code={`let a = 5, b = 1; console.log(a | b);`} />
        <OutputBox>5</OutputBox>
      </section>

      {/* 3️⃣ Bitwise XOR */}
      <section id="bit_xor" className="method-section">
        <h2>
          3️⃣ Bitwise XOR (<code className="highlight_code">^</code>)
        </h2>
        <p>
          Performs a logical XOR operation on each pair of bits. The result is 1
          only if bits are different.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let a = 5;  // 0101
let b = 3;  // 0011
console.log(a ^ b); // 0110 -> 6`}
          />
        </div>
        <TryItButton code={`let a = 5, b = 3; console.log(a ^ b);`} />
        <OutputBox>6</OutputBox>
      </section>

      {/* 4️⃣ Bitwise NOT */}
      <section id="bit_not" className="method-section">
        <h2>
          4️⃣ Bitwise NOT (<code className="highlight_code">~</code>)
        </h2>
        <p>
          Inverts all bits of a number. Equivalent to{" "}
          <code className="highlight_code">-(n + 1)</code>.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let a = 5;  // 0101
console.log(~a); // 1010 -> -6`}
          />
        </div>
        <TryItButton code={`let a = 5; console.log(~a);`} />
        <OutputBox>-6</OutputBox>
      </section>

      {/* 5️⃣ Left Shift */}
      <section id="bit_left" className="method-section">
        <h2>
          5️⃣ Left Shift (<code className="highlight_code">&lt;&lt;</code>)
        </h2>
        <p>
          Shifts bits to the left and adds zeros from the right. Each shift
          multiplies the number by 2.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let a = 5;  // 0101
console.log(a << 1); // 1010 -> 10`}
          />
        </div>
        <TryItButton code={`let a = 5; console.log(a << 1);`} />
        <OutputBox>10</OutputBox>
      </section>

      {/* 6️⃣ Right Shift */}
      <section id="bit_right" className="method-section">
        <h2>
          6️⃣ Right Shift (<code className="highlight_code">&gt;&gt;</code>)
        </h2>
        <p>
          Shifts bits to the right and preserves the sign bit. Each shift
          divides the number by 2 (rounding down).
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let a = 5;  // 0101
console.log(a >> 1); // 0010 -> 2`}
          />
        </div>
        <TryItButton code={`let a = 5; console.log(a >> 1);`} />
        <OutputBox>2</OutputBox>
      </section>

      {/* 7️⃣ Unsigned Right Shift */}
      <section id="bit_unsigned" className="method-section">
        <h2>
          7️⃣ Unsigned Right Shift (
          <code className="highlight_code">&gt;&gt;&gt;</code>)
        </h2>
        <p>
          Shifts bits to the right and fills left bits with zeros. Always
          results in a positive number.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let a = -5;
console.log(a >>> 1); // 2147483645`}
          />
        </div>
        <TryItButton code={`let a = -5; console.log(a >>> 1);`} />
        <OutputBox>2147483645</OutputBox>
      </section>

      {/* 🚀 Next Steps */}
      <div
        className="next-steps"
        style={{
          background: "linear-gradient(135deg, #cffafe, #a5f3fc)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h3 style={{ color: "#0e7490" }}>🚀 Next Steps</h3>
        <ul>
          <li>
            Explore <code className="highlight_code">>>></code> vs{" "}
            <code className="highlight_code">>></code> in signed vs unsigned
            integers
          </li>
          <li>
            Learn about <code className="highlight_code">BigInt</code> and
            bitwise limitations
          </li>
          <li>
            Try using bitwise operations for flags, permissions, and color masks
          </li>
        </ul>
      </div>
    </div>
  );
}
